import React, { useMemo } from 'react';
import { ReactFlowProvider } from 'reactflow';
import Controller, { ControllerProps } from './Controller';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ToastProvider } from './contexts/ToastContext';
import { parseWorkflowProject, parseProjectRuleSets } from './utils/pssHelper';
import { registerNodes } from './utils/nodeRegistry';
import { WorkflowProject, ProcessDefinition, NamedRuleSet } from './types';
import './styles/index.css';

// Re-export types for consuming applications
export * from './types';
export type { ControllerProps } from './Controller';
export type { NodeRegistration, ExtensionHandler } from './utils/nodeRegistry';
export type {
  EditorAiCapability,
  EditorToolResult,
  EditorToolDescriptor,
  WorkflowModelAccess,
} from './utils/aiCapability';

/**
 * Check if the data is a WorkflowProject (PSS format) vs ProcessDefinition[]
 */
const isWorkflowProject = (data: unknown): data is WorkflowProject => {
  return (
    typeof data === 'object' &&
    data !== null &&
    'Processes' in data &&
    Array.isArray((data as WorkflowProject).Processes)
  );
};

/**
 * Wrapper component for the Workflow Editor that provides necessary contexts.
 * This is the main entry point for the CDN bundle.
 *
 * Accepts initialData as either:
 * - ProcessDefinition[] (editor format)
 * - WorkflowProject (PSS format) - will be automatically parsed
 */
export const WorkflowEditorWrapper: React.FC<ControllerProps & { initialData?: ProcessDefinition[] | WorkflowProject }> = (props) => {
  const { initialData, customNodes, ...restProps } = props;

  // Register custom nodes BEFORE parsing so resolveEditorType works for loading
  if (customNodes) registerNodes(customNodes);

  // Parse initialData if it's in WorkflowProject (PSS) format
  const parsedInitialData = useMemo(() => {
    if (!initialData) return undefined;
    if (isWorkflowProject(initialData)) {
      return parseWorkflowProject(initialData);
    }
    return initialData;
  }, [initialData]);

  // Extract the error-handling ruleset library from the project Resources.
  // Falls back to any explicitly-provided initialRuleSets prop.
  const parsedRuleSets = useMemo<NamedRuleSet[] | undefined>(() => {
    if (initialData && isWorkflowProject(initialData)) {
      return parseProjectRuleSets(initialData);
    }
    return restProps.initialRuleSets;
  }, [initialData, restProps.initialRuleSets]);

  return (
    <ErrorBoundary>
      <ToastProvider>
        <ReactFlowProvider>
          <div style={{ width: '100%', height: '100%' }}>
            <Controller {...restProps} customNodes={customNodes} initialData={parsedInitialData} initialRuleSets={parsedRuleSets} />
          </div>
        </ReactFlowProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
};

export default WorkflowEditorWrapper;
